import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * LiveContext — tracks active "Go Live" sessions across all of the
 * user's groups. Powers the red pulsing AlertBar takeover banner and
 * the in-app "🔴 Live Now" rail.
 *
 * Polls GET /api/live/active every 8 seconds while the user is signed in.
 * That's loose enough to be free on the backend but tight enough that
 * a family member tapping "Go Live" feels effectively-instant to the
 * rest of the group.
 *
 * Exposes:
 *   sessions          — array of active live sessions visible to me
 *   primary           — the most recent active session, or null
 *   activeRoomId      — the session_id currently open in the LiveRoomModal
 *   openRoom(sid)     — open the Live Room modal for a given session
 *   closeRoom()       — close the modal (does NOT leave the session)
 *   refresh()         — manually re-poll (call after start / stop)
 */
const LiveContext = createContext({
  sessions: [],
  primary: null,
  activeRoomId: null,
  openRoom: () => {},
  closeRoom: () => {},
  refresh: () => {},
});

const POLL_MS = 8_000;

export function LiveProvider({ children }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const { data } = await api.get("/live/active");
      setSessions(data?.items || []);
    } catch (e) {
      // Stay silent on transient failures so the banner doesn't flicker.
      console.warn("[live] refresh failed:", e?.message || e);
    }
  }, [user?.user_id]);

  // Reset when the signed-in user changes (login / logout). Derived-during-
  // render pattern avoids the set-state-in-effect lint.
  const [seenUid, setSeenUid] = useState(user?.user_id || null);
  if ((user?.user_id || null) !== seenUid) {
    setSeenUid(user?.user_id || null);
    setSessions([]);
    setActiveRoomId(null);
  }

  useEffect(() => {
    if (!user?.user_id) return;
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [user?.user_id, refresh]);

  const openRoom = useCallback((sid) => setActiveRoomId(sid || null), []);
  const closeRoom = useCallback(() => setActiveRoomId(null), []);

  const primary = sessions[0] || null;

  const value = useMemo(
    () => ({ sessions, primary, activeRoomId, openRoom, closeRoom, refresh }),
    [sessions, primary, activeRoomId, openRoom, closeRoom, refresh]
  );

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export function useLive() {
  return useContext(LiveContext);
}
